import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { GamificationProfile, GamificationNotification } from '../types';
import { gamificationService } from '../services/gamificationService';
import { reportError } from '../lib/errorHandling';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { UserRole } from '../types';

interface GamificationContextType {
  profile: GamificationProfile | null;
  notifications: GamificationNotification[];
  isLoading: boolean;
  onLessonComplete: (completedLessonCount: number) => Promise<void>;
  onCourseComplete: (totalPoints: number, completedCourseCount: number) => Promise<void>;
  onQuizResult: (score: number) => Promise<void>;
  refreshProfile: (totalPoints: number) => Promise<void>;
  dismissNotification: (id: string) => void;
}

const GamificationContext = createContext<GamificationContextType | undefined>(undefined);

const noopContext: GamificationContextType = {
  profile: null,
  notifications: [],
  isLoading: false,
  onLessonComplete: async () => {},
  onCourseComplete: async () => {},
  onQuizResult: async () => {},
  refreshProfile: async () => {},
  dismissNotification: () => {},
};

export const GamificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { features } = useTenant();
  const gamificationEnabled = features.gamification;
  const [profile, setProfile] = useState<GamificationProfile | null>(null);
  const [notifications, setNotifications] = useState<GamificationNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [perfectQuizCount, setPerfectQuizCount] = useState(0);

  // On auth mount: record daily activity + load profile
  useEffect(() => {
    if (!gamificationEnabled || !user || user.role !== UserRole.STUDENT) {
      setProfile(null);
      return;
    }

    const init = async () => {
      setIsLoading(true);
      try {
        // Record daily activity (streak)
        const { streak, streakMilestone } = await gamificationService.recordActivity(user.id);

        // Load profile (we don't have totalPoints here, so we start with 0 and let WelcomePage refresh)
        const gamProfile = await gamificationService.getGamificationProfile(user.id, 0);
        // Override streak with fresh data from recordActivity
        gamProfile.streak = streak;
        setProfile(gamProfile);

        // Streak milestone notification
        if (streakMilestone) {
          addNotification({
            id: crypto.randomUUID(),
            type: 'streak_milestone',
            title: 'Streak Milestone!',
            message: `${streakMilestone}-day streak! Keep it going!`,
            streak: streakMilestone,
          });
        }

        // Check streak badges
        const streakNotifs = await gamificationService.checkAndAwardBadges(user.id, {
          currentStreak: streak.currentStreak,
        });
        if (streakNotifs.length > 0) {
          setNotifications((prev) => [...prev, ...streakNotifs]);
        }
      } catch (error) {
        reportError('GamificationContext.init', error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, gamificationEnabled]);

  const addNotification = useCallback((notif: GamificationNotification) => {
    setNotifications((prev) => [...prev, notif]);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const refreshProfile = useCallback(async (totalPoints: number) => {
    if (!user) return;
    try {
      const gamProfile = await gamificationService.getGamificationProfile(user.id, totalPoints);
      setProfile(gamProfile);
    } catch (error) {
      reportError('GamificationContext.refreshProfile', error);
    }
  }, [user]);

  const onLessonComplete = useCallback(async (completedLessonCount: number) => {
    if (!user) return;
    try {
      const notifs = await gamificationService.checkAndAwardBadges(user.id, {
        completedLessonCount,
        currentStreak: profile?.streak.currentStreak,
      });
      if (notifs.length > 0) {
        setNotifications((prev) => [...prev, ...notifs]);
      }
    } catch (error) {
      reportError('GamificationContext.onLessonComplete', error);
    }
  }, [user, profile]);

  const onCourseComplete = useCallback(async (totalPoints: number, completedCourseCount: number) => {
    if (!user) return;
    try {
      // Update level
      const { level, leveledUp } = await gamificationService.updateUserLevel(user.id, totalPoints);

      if (leveledUp) {
        addNotification({
          id: crypto.randomUUID(),
          type: 'level_up',
          title: 'Level Up!',
          message: `You reached level ${level}!`,
          level,
        });
      }

      // Check all badges
      const notifs = await gamificationService.checkAndAwardBadges(user.id, {
        completedCourseCount,
        totalPoints,
        level,
        currentStreak: profile?.streak.currentStreak,
        perfectQuizCount,
      });
      if (notifs.length > 0) {
        setNotifications((prev) => [...prev, ...notifs]);
      }

      // Refresh profile
      await refreshProfile(totalPoints);
    } catch (error) {
      reportError('GamificationContext.onCourseComplete', error);
    }
  }, [user, profile, perfectQuizCount, addNotification, refreshProfile]);

  const onQuizResult = useCallback(async (score: number) => {
    if (!user) return;
    if (score === 100) {
      const newCount = perfectQuizCount + 1;
      setPerfectQuizCount(newCount);
      const notifs = await gamificationService.checkAndAwardBadges(user.id, {
        perfectQuizCount: newCount,
      });
      if (notifs.length > 0) {
        setNotifications((prev) => [...prev, ...notifs]);
      }
    }
  }, [user, perfectQuizCount]);

  // Listen for lesson-complete events dispatched from CourseContext
  useEffect(() => {
    if (!gamificationEnabled || !user || user.role !== UserRole.STUDENT) return;

    const handleLessonComplete = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      onLessonComplete(detail.completedLessonCount);
    };

    window.addEventListener('gamification:lesson-complete', handleLessonComplete);
    return () => window.removeEventListener('gamification:lesson-complete', handleLessonComplete);
  }, [gamificationEnabled, user, onLessonComplete]);

  // Listen for course-complete events dispatched from CourseContext (0->100 transition)
  useEffect(() => {
    if (!gamificationEnabled || !user || user.role !== UserRole.STUDENT) return;

    const handleCourseComplete = async (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Generic course-count badges (Graduate/Scholar/Overachiever) + level-up.
      await onCourseComplete(detail.totalPoints ?? 0, detail.completedCourseCount ?? 0);
      // This course's custom completion badge, if any.
      try {
        const notif = await gamificationService.awardCourseBadge(user.id, detail.courseId);
        if (notif) setNotifications((prev) => [...prev, notif]);
      } catch (error) {
        reportError('GamificationContext.awardCourseBadge', error, { courseId: detail.courseId });
      }
    };

    window.addEventListener('gamification:course-complete', handleCourseComplete);
    return () => window.removeEventListener('gamification:course-complete', handleCourseComplete);
  }, [gamificationEnabled, user, onCourseComplete]);

  return (
    <GamificationContext.Provider
      value={gamificationEnabled ? {
        profile,
        notifications,
        isLoading,
        onLessonComplete,
        onCourseComplete,
        onQuizResult,
        refreshProfile,
        dismissNotification,
      } : noopContext}
    >
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (context === undefined) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};
